/**
 * DailyBudgetCard
 * Shows the user's daily spending budget based on cashflow forecast.
 */

import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';
import type { ForecastData } from '../../api/endpoints/budget';

interface Props {
  forecast: ForecastData | null;
  isLoading: boolean;
}

export default function DailyBudgetCard({ forecast, isLoading }: Props) {
  if (!forecast || isLoading) return null;

  const daily_budget = forecast.daily_budget ?? 0;
  const current_balance = forecast.current_balance ?? 0;
  const expected_debits_remaining = forecast.expected_debits_remaining ?? 0;
  const days_remaining = forecast.days_remaining ?? 0;
  const days_elapsed = forecast.days_elapsed ?? 0;
  const days_in_month = (days_elapsed + days_remaining) || 30;
  const available = current_balance - expected_debits_remaining;
  const savings_goal = forecast.savings_goal;
  const progressPercent = days_in_month > 0 ? Math.round((days_elapsed / days_in_month) * 100) : 0;

  const budgetColor = daily_budget > 20 ? '#4CAF50' : daily_budget > 10 ? '#FF9800' : '#EF4444';

  return (
    <View style={styles.card}>
      <Text style={styles.header}>Tu peux dépenser aujourd'hui :</Text>
      <Text style={[styles.bigNumber, { color: budgetColor }]}>
        {Math.round(daily_budget)} €
      </Text>

      <View style={styles.row}>
        <Text style={styles.rowLabel}>Solde total</Text>
        <Text style={styles.rowValue}>{current_balance.toLocaleString('fr-FR')} €</Text>
      </View>
      <View style={styles.row}>
        <Text style={styles.rowLabel}>À venir</Text>
        <Text style={[styles.rowValue, { color: '#EF4444' }]}>-{expected_debits_remaining.toLocaleString('fr-FR')} €</Text>
      </View>
      <View style={styles.row}>
        <Text style={styles.rowLabel}>Dispo</Text>
        <Text style={styles.rowValue}>{available.toLocaleString('fr-FR')} € · {days_remaining}j restants</Text>
      </View>

      {/* Progress bar */}
      <View style={styles.progressContainer}>
        <View style={styles.progressBackground}>
          <View style={[styles.progressFill, { width: `${Math.min(progressPercent, 100)}%` }]} />
        </View>
        <Text style={styles.progressText}>{progressPercent}% du mois écoulé</Text>
      </View>

      {savings_goal != null && savings_goal > 0 && (
        <View style={styles.savingsRow}>
          <Text style={styles.savingsText}>
            Objectif épargne : {savings_goal.toLocaleString('fr-FR')} €/mois
          </Text>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 12,
    padding: spacing.md,
    marginBottom: spacing.md,
  },
  header: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginBottom: spacing.xs,
  },
  bigNumber: {
    fontSize: 36,
    fontWeight: typography.fontWeight.bold,
    marginBottom: spacing.md,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4,
  },
  rowLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
  },
  rowValue: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    color: colors.text,
  },
  progressContainer: {
    marginTop: spacing.md,
  },
  progressBackground: {
    height: 6,
    backgroundColor: colors.border,
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: colors.primary,
    borderRadius: 3,
  },
  progressText: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    marginTop: 4,
  },
  savingsRow: {
    marginTop: spacing.sm,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  savingsText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontWeight: typography.fontWeight.medium,
  },
});
